/**
 * Shrink a picked icon image to the slot's render target BEFORE uploading it.
 *
 * WHY: the server renders every background down to 1024² and every foreground to
 * 512² the moment it lands, so sending the original 4000²-pixel file uploads
 * megabytes that are decoded and immediately thrown away. Shrinking here means
 * the wire carries ~200 KB instead of ~2 MB, the server decodes a small image,
 * and — the point of it — the upload cap can be low without refusing ordinary
 * photos: a 4001² JPEG is ~1.15 MB and a 1024² true-colour PNG ~1.25 MB, both of
 * which a tight cap would otherwise reject outright.
 *
 * NOT a trust boundary. The server re-sniffs the format, re-checks the size and
 * re-encodes regardless; this only changes which bytes we bother to send. Every
 * failure path returns the ORIGINAL file and lets the server decide.
 *
 * Deliberately mirrors the server's geometry (icon-assets.ts) so the server's own
 * resize is a no-op on our output rather than a second resample:
 *   - background → `fit: cover` to 1024², so we scale by the SHORT edge. Scaling
 *     by the long edge would leave the short edge under 1024 and force the server
 *     to UPSCALE — worse than not shrinking at all.
 *   - foreground → `fit: inside` 512², so we scale by the LONG edge.
 */
import {
  MINIAPP_ICON_BG_RENDER_DIM,
  MINIAPP_ICON_FG_RENDER_DIM,
  PDAPP_PORTABLE_ASSET_MAX_BYTES,
  PDAPP_PORTABLE_BG_W,
  PDAPP_PORTABLE_FG_DIM,
} from '@papan/types'

/** True for an SVG pick (by type, or by name when the browser reports no type). */
function isSvg(file: File): boolean {
  return /^image\/svg/i.test(file.type) || (!file.type && /\.svg$/i.test(file.name))
}

/** Swap a filename's extension, keeping the stem the user recognises. */
function reExt(name: string, ext: string): string {
  return `${name.replace(/\.[^./\\]+$/, '') || 'image'}.${ext}`
}

/**
 * Returns a shrunk File, or the original when shrinking is impossible or wouldn't
 * help. Never throws.
 */
export async function downscaleIconImage(kind: 'bg' | 'fg', file: File): Promise<File> {
  // SVG is vector: there's nothing to downscale, and rasterising it here would
  // destroy the resolution independence the server's rasteriser wants. Its own
  // (much tighter) byte cap still applies.
  if (isSvg(file)) return file
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file

  let bmp: ImageBitmap | undefined
  try {
    // `from-image` bakes EXIF orientation into the pixels, matching the server's
    // `.rotate()`; without it a phone photo would shrink in the wrong orientation.
    bmp = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const target = kind === 'bg' ? MINIAPP_ICON_BG_RENDER_DIM : MINIAPP_ICON_FG_RENDER_DIM
    const edge = kind === 'bg'
      ? Math.min(bmp.width, bmp.height) // cover  → short edge
      : Math.max(bmp.width, bmp.height) // inside → long edge
    // Never upscale: a source smaller than the target is already as good as it gets.
    const scale = Math.min(1, target / Math.max(1, edge))

    // A foreground that needs no resize gains nothing from a re-encode (and a
    // JPEG→PNG round-trip would usually grow it). A BACKGROUND still re-encodes at
    // scale 1, because the server flattens it to JPEG anyway and that is exactly
    // where a 1.25 MB lossless PNG becomes ~190 KB.
    if (kind === 'fg' && scale === 1) return file

    const w = Math.max(1, Math.round(bmp.width * scale))
    const h = Math.max(1, Math.round(bmp.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(bmp, 0, 0, w, h)

    // bg is flattened to an opaque JPEG server-side, so JPEG loses nothing here.
    // fg MUST stay PNG — a JPEG would flatten away the transparency the compositor
    // needs. Quality 0.9 leaves headroom above the server's final q85 re-encode.
    const [mime, ext, quality] = kind === 'bg'
      ? (['image/jpeg', 'jpg', 0.9] as const)
      : (['image/png', 'png', undefined] as const)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, quality))
    if (!blob) return file
    // If the "shrunk" file isn't actually smaller, keep the original — no reason to
    // spend a lossy generation for nothing.
    if (blob.size >= file.size) return file
    return new File([blob], reExt(file.name, ext), { type: mime })
  } catch {
    return file // decode/encode failed → let the server judge the original
  } finally {
    bmp?.close()
  }
}

/** Thrown when a picked image cannot travel inside portable metadata. The author
 *  is standing right there, so this is a sentence for them, not a log line. */
export class PortableIconAssetError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PortableIconAssetError'
  }
}

/**
 * Re-encode a picked image into a `data:` URI that portable metadata can carry
 * (#788) — the BROWSER half of what the server export does with sharp.
 *
 * Three things make this different from `downscaleIconImage` above, and each of
 * them was a real defect first:
 *
 *   • **Everything is rasterised, SVG included.** The SDK's renderer accepts only
 *     `data:image/png` and `data:image/jpeg` — an SVG data URI would carry script
 *     into someone else's page, so it `fail()`s rather than degrading. Passing an
 *     SVG through unchanged (which is right for an upload, where the server
 *     rasterises it later) produced metadata the renderer refused, and the author
 *     got a permanently empty 28px box with one console line.
 *   • **The slot decides the format, not the file type.** A foreground MUST stay
 *     PNG; encoding one as JPEG composites its transparency onto black, which
 *     lands as an opaque square over the gradient the author chose.
 *   • **There is a budget.** This rides a Y.Doc edit and ships base64'd to every
 *     visitor of the author's app. Over it, this REFUSES — the export can quietly
 *     drop an asset because nobody is watching, but here there is a person who
 *     can pick a different picture.
 *
 * Geometry and ceiling come from `@papan/types` so the two producers cannot
 * drift. Throws `PortableIconAssetError` with something the author can act on.
 */
export async function encodePortableIconAsset(kind: 'bg' | 'fg', file: File): Promise<string> {
  if (typeof document === 'undefined') throw new PortableIconAssetError('Images need a browser to prepare.')
  const url = URL.createObjectURL(file)
  try {
    // An <img>, not `createImageBitmap`: it is the one decoder that takes SVG as
    // well as raster in every browser we support. Scripts never run in an image
    // context, so a hostile SVG is inert here — and it is rasterised away anyway.
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new PortableIconAssetError('That image could not be read.'))
      el.src = url
    })
    const maxW = kind === 'bg' ? PDAPP_PORTABLE_BG_W : PDAPP_PORTABLE_FG_DIM
    // A background may be tall; clamp BOTH axes, or a deliberately long image
    // keeps its byte cost unbounded in the other direction (the export clamps to
    // `width × width * 2` for the same reason).
    const maxH = kind === 'bg' ? PDAPP_PORTABLE_BG_W * 2 : PDAPP_PORTABLE_FG_DIM
    // An SVG with no intrinsic size reports 0 — draw it AT the target instead of
    // dividing by zero.
    const nw = img.naturalWidth || maxW
    const nh = img.naturalHeight || maxH
    const scale = Math.min(1, maxW / nw, maxH / nh)
    const w = Math.max(1, Math.round(nw * scale))
    const h = Math.max(1, Math.round(nh * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new PortableIconAssetError('That image could not be prepared.')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(img, 0, 0, w, h)
    const [mime, quality] = kind === 'bg' ? (['image/jpeg', 0.78] as const) : (['image/png', undefined] as const)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, quality))
    if (!blob) throw new PortableIconAssetError('That image could not be prepared.')
    if (blob.size > PDAPP_PORTABLE_ASSET_MAX_BYTES) {
      throw new PortableIconAssetError(
        `That picture is too detailed to travel inside an icon (${Math.round(blob.size / 1024)} KB after shrinking, limit ${Math.round(PDAPP_PORTABLE_ASSET_MAX_BYTES / 1024)} KB). Try a simpler one.`,
      )
    }
    return await new Promise<string>((resolve, reject) => {
      const r = new FileReader()
      r.onload = () => resolve(String(r.result))
      r.onerror = () => reject(new PortableIconAssetError('That image could not be read.'))
      r.readAsDataURL(blob)
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}
